import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Names that must NEVER appear in the public storefront grid / chips /
 *  Sell dropdown. Kept in the DB so legacy products still resolve. */
const HIDDEN_CATEGORY_NAMES = new Set(["Others", "Other"]);

/** Client-side defensive filter mirroring the backend `/categories` contract.
 *  Ensures a stale service-worker cached response can't leak "Others" back
 *  into the UI. Admin views bypass this filter. */
export function filterVisibleCategories(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((c) => {
    if (!c || typeof c.name !== "string") return false;
    if (HIDDEN_CATEGORY_NAMES.has(c.name)) return false;
    if (c.is_hidden === true || c.isHidden === true) return false;
    if (c.enabled === false || c.isActive === false) return false;
    // Empty categories (products_count===0) are intentionally kept visible so
    // users can discover them and sellers can list into them.
    return true;
  });
}

/**
 * Fetches dynamic marketplace categories from /api/categories (public, enabled-only).
 * Returns: { categories: [...], loading, error, refresh }
 * Each category: { id, name, slug, icon, image_url, display_order, enabled, products_count }
 */
export function useCategories({ admin = false, featured = false } = {}) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (admin) {
        url = "/admin/categories";
      } else if (featured) {
        url = "/categories?featured=true";
      } else {
        url = "/categories";
      }
      const r = await api.get(url);
      const raw = Array.isArray(r.data) ? r.data : [];
      // Admin panel needs the full list including hidden/disabled to manage them.
      setCategories(admin ? raw : filterVisibleCategories(raw));
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [admin, featured]);

  useEffect(() => { refresh(); }, [refresh]);

  return { categories, loading, error, refresh };
}

/** Category names (strings) used by Sell-a-Product dropdowns. */
export function useCategoryNames() {
  const { categories, loading } = useCategories();
  return { names: categories.map(c => c.name), loading };
}
